import { liveQuery } from 'dexie';
import { momentSyncSchema, type MomentConfigState, type MomentDelivery, type MomentSnapshot } from '../../../server/src/modules/moments/contracts';
import { MAX_TOKEN_LIMIT } from '../../../server/src/modules/gateway/contracts';
import { ApiClientError, apiClient } from '../../core/api/client';
import { getDb } from '../../core/db';
import { newId } from '../../core/ids';
import { SERVER_PROVIDER_ID } from '../../core/providers';
import { providerPurpose } from '../../core/providers/types';
import type { Conversation, MomentSyncConfig, Preset, ProviderConfig } from '../../core/types';
import { getFeatureFlags } from '../../core/featureFlags';
import { normalizeModelSettings } from '../../core/settings';
import { resolveRoleConversation, validateRoleModelLimits } from '../../core/roleModel';
import { assembleChatMessages } from '../chat/contextAssembly';
import { loadOfficialCatalog } from '../chat/officialModelCatalog';
import { readSecret } from '../providers/secretStore';
import { automationAllowsBubble, automationAllowsMoment, buildAutomationInstructions } from '../automation/richOutput';
import { putPreset } from '../../core/repositories/presetRepo';
import { getMomentAccountId, setMomentAccountId, subscribeMomentAccount } from './accountScope';

const DEVICE_KEY = 'zhixin.moments.device';
const SYNC_INTERVAL_MS = 30_000;
const ACTIVITY_INTERVAL_MS = 60_000;
const inFlight = new WeakMap<ReturnType<typeof getDb>, Promise<void>>();
const syncErrors = new Map<string, string>();
export const AUTOMATION_SYNC_STATUS_EVENT = 'zhixin:automation-sync-status';

export function getAutomationSyncError(): string | null {
  const userId = getMomentAccountId();
  return userId ? syncErrors.get(userId) ?? null : null;
}

function reportSyncError(userId: string, error?: Error): void {
  const previous = syncErrors.get(userId);
  if (error) syncErrors.set(userId, error.message);
  else syncErrors.delete(userId);
  if (previous !== error?.message) window.dispatchEvent(new Event(AUTOMATION_SYNC_STATUS_EVENT));
}

function automationEnabled(preset: Preset): boolean {
  return automationAllowsBubble(preset.automationMode) || automationAllowsMoment(preset.automationMode);
}

function serialized(db: ReturnType<typeof getDb>, action: () => Promise<void>): Promise<void> {
  const task = (inFlight.get(db) ?? Promise.resolve()).catch(() => undefined).then(action);
  inFlight.set(db, task);
  void task.finally(() => { if (inFlight.get(db) === task) inFlight.delete(db); }).catch(() => undefined);
  return task;
}

function deviceId(): string {
  let id = localStorage.getItem(DEVICE_KEY);
  if (!id) { id = newId(); localStorage.setItem(DEVICE_KEY, id); }
  return id;
}

function active(userId: string): void {
  if (getMomentAccountId() !== userId) throw new Error('ACCOUNT_CHANGED');
}

function request<T>(userId: string, path: string, method: string, body?: unknown): Promise<T> {
  active(userId);
  return apiClient.request<T>(`/v1/moments${path}`, {
    method, headers: { 'x-moments-account': userId }, ...(body === undefined ? {} : { body }),
  });
}

function selectedModel(providerId: string | undefined, modelValue: string | undefined, providers: ProviderConfig[]): MomentSnapshot['model'] | null {
  const model = modelValue?.trim();
  if (!model) return null;
  if (providerId === SERVER_PROVIDER_ID) {
    const matches = [...model.matchAll(/\(([^()]+)\)/g)];
    return { kind: 'official', model: matches.at(-1)?.[1] ?? model };
  }
  const provider = providers.find(row => row.id === providerId && row.enabled);
  if (!provider) return null;
  const apiKey = provider.apiKeyRef.trim() ? readSecret(provider.apiKeyRef) : null;
  return {
    kind: 'custom', model,
    provider: { kind: provider.kind, ...(provider.protocol ? { protocol: provider.protocol } : {}), baseUrl: provider.baseUrl,
      ...(provider.headers ? { headers: provider.headers } : {}),
      ...(apiKey === null ? {} : { apiKey }) },
  };
}

export async function buildMomentSnapshot(preset: Preset, conversations: Conversation[], providers: ProviderConfig[]): Promise<MomentSnapshot | null> {
  const conversation = await configuredConversation(preset, conversations, providers);
  if (!conversation) return null;
  const model = selectedModel(conversation.providerId, conversation.model, providers);
  if (!model) return null;
  const history = (await getDb().messages.where('conversationId').equals(conversation.id).sortBy('createdAt'))
    .filter(message => message.status === 'done' && (message.role === 'user' || message.role === 'assistant')).slice(-20);
  const assembled = assembleChatMessages({ conversationId: conversation.id, messages: history, preset });
  const settings = preset.modelConfig === undefined ? normalizeModelSettings(conversation.settings) : conversation.settings;
  const image = preset.autoImageEnabled ? selectedModel(preset.imageProviderId, preset.imageModel, providers) : null;
  const imageInstructions = buildAutomationInstructions(preset, { autoText: false, autoImage: image !== null, autoTts: false }, getFeatureFlags());
  const systemPrompt = [assembled.systemMessage?.content ?? settings.systemPrompt, imageInstructions].filter(Boolean).join('\n\n')
    || `Act as ${preset.name}.`;
  if (systemPrompt.length > 256_000) throw new Error('朋友圈角色设定超出同步上限');
  return {
    presetId: preset.id, authorName: preset.name,
    intimacy: { presetId: preset.id },
    ...(preset.automationMode && preset.automationMode !== 'reply_only' ? { automationMode: preset.automationMode } : {}),
    automation: { quietPeriods: preset.quietPeriods ?? [], autoImageEnabled: preset.autoImageEnabled,
      imageGenerationPolicy: preset.imageGenerationPolicy, imageProviderId: preset.imageProviderId, imageModel: preset.imageModel,
      autoTtsEnabled: preset.autoTtsEnabled, ttsGenerationPolicy: preset.ttsGenerationPolicy,
      ttsProviderId: preset.ttsProviderId, ttsModel: preset.ttsModel, ttsVoiceMode: preset.ttsVoiceMode,
      ttsAutoVoiceDesign: preset.ttsAutoVoiceDesign },
    ...(preset.avatar ? { authorAvatar: preset.avatar } : {}),
    systemPrompt,
    messages: history.map(message => ({ role: message.role as 'user' | 'assistant', content: message.content.slice(0, 64_000) })),
    model,
    settings: { temperature: settings.temperature, topP: settings.topP, maxTokens: settings.maxOutputTokens,
      reasoningEffort: settings.thinkingLevel, contextMaxTokens: settings.maxContext, stream: settings.stream },
    ...(image ? { image } : {}), timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  };
}

async function configuredConversation(preset: Preset, conversations: Conversation[], providers: ProviderConfig[]): Promise<Conversation | undefined> {
  if (preset.modelConfig === undefined) return momentConversation(preset.id, conversations, providers);
  const existing = conversations.filter(row => row.presetId === preset.id && row.modelKind !== 'image')
    .sort((a, b) => b.updatedAt - a.updatedAt)[0];
  const effective = resolveRoleConversation(preset, existing, providers);
  if (effective?.providerId === SERVER_PROVIDER_ID) {
    const catalog = await loadOfficialCatalog();
    const option = catalog.chat.find(row => row.slug === effective.model || row.label === effective.model);
    if (!option) throw new Error('角色官方文字模型已下架或不可用，请重新选择角色模型');
    validateRoleModelLimits(preset.modelConfig, option);
  }
  return effective;
}

function momentConversation(presetId: string, conversations: Conversation[], providers: ProviderConfig[]): Conversation | undefined {
  return conversations.filter(row => row.presetId === presetId && row.modelKind !== 'image').sort((a, b) => b.updatedAt - a.updatedAt)
    .find(row => selectedModel(row.providerId, row.model, providers) !== null && (row.providerId === SERVER_PROVIDER_ID ||
      providers.some(provider => provider.id === row.providerId && provider.enabled && providerPurpose(provider) === 'chat')));
}

async function fingerprint(value: unknown): Promise<string> {
  const bytes = new TextEncoder().encode(JSON.stringify(value));
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(digest), byte => byte.toString(16).padStart(2, '0')).join('');
}

function validateSnapshot(snapshot: MomentSnapshot): void {
  const result = momentSyncSchema.safeParse(JSON.parse(JSON.stringify({ expectedVersion: 0, sourceRevision: 'validate', snapshot })));
  if (!result.success) {
    const fields = [...new Set(result.error.issues.map(issue => issue.path.join('.')))];
    if (fields.includes('snapshot.settings.maxTokens')) {
      throw new Error(`最大输出 token 必须是 1 到 ${MAX_TOKEN_LIMIT} 之间的整数（snapshot.settings.maxTokens），请检查对话中的模型参数`);
    }
    throw new Error(`自动化配置校验失败：${fields.join('、')}，请检查角色或模型设置`);
  }
}

async function structureFingerprint(preset: Preset | undefined, snapshot: MomentSnapshot | null, conversation: Conversation | undefined): Promise<string> {
  return fingerprint({ preset: preset ?? null, model: snapshot?.model ?? null,
    settings: preset?.modelConfig?.settings ?? conversation?.settings ?? null, image: snapshot?.image ?? null, timeZone: snapshot?.timeZone ?? null });
}

/** Explicit edits and polling share a queue so old local data cannot undo a save. */
export function savePresetWithAutomationSync(preset: Preset): Promise<void> {
  const db = getDb();
  const userId = getMomentAccountId();
  return serialized(db, async () => {
    const previous = await db.presets.get(preset.id);
    const owned = await db.momentSyncConfigs.get(preset.id);
    let cloud: MomentConfigState | undefined;
    let remoteChecked = false;
    if (!automationEnabled(preset) && !automationEnabled(previous ?? preset) && !owned && userId) {
      active(userId);
      if (!navigator.onLine) throw new Error('当前离线，联网后重试保存');
      const result = await request<{ configs: MomentConfigState[] }>(userId, '/configs', 'GET');
      cloud = result.configs.find(row => row.presetId === preset.id);
      remoteChecked = true;
    }
    const disablingAutomation = !automationEnabled(preset) && (automationEnabled(previous ?? preset) || owned?.enabled === true || cloud?.enabled === true);
    if (preset.modelConfig !== undefined && !automationEnabled(preset) && !disablingAutomation) {
      await configuredConversation(preset, [], await db.providers.toArray());
    }
    if (!automationEnabled(preset) && !automationEnabled(previous ?? preset) && !owned && cloud?.enabled !== true) {
      await putPreset(preset);
      return;
    }
    if (!userId) throw new Error('请先登录，再保存自动化配置');
    active(userId);
    if (!navigator.onLine) throw new Error('当前离线，联网后重试保存');
    if (owned && owned.userId !== userId) throw new Error('该角色已绑定其他账号，请切换原账号后保存');
    const [conversations, providers] = await Promise.all([db.conversations.toArray(), db.providers.toArray()]);
    const snapshot = automationEnabled(preset) ? await buildMomentSnapshot(preset, conversations, providers) : null;
    if (automationEnabled(preset) && !snapshot) throw new Error('请在角色编辑页的模型配置中选择可用的文字模型，再重试保存');
    if (snapshot) validateSnapshot(snapshot);
    if (!remoteChecked) {
      const { configs } = await request<{ configs: MomentConfigState[] }>(userId, '/configs', 'GET');
      cloud = configs.find(row => row.presetId === preset.id);
    }
    const revision = newId();
    const result = await request<{ version: number }>(userId, `/configs/${encodeURIComponent(preset.id)}`, snapshot ? 'PUT' : 'DELETE', {
      expectedVersion: cloud?.version ?? 0, sourceRevision: revision, ...(snapshot ? { snapshot } : {}),
    });
    active(userId);
    if (!Number.isInteger(result.version) || result.version < 1) throw new Error('服务器未确认配置，请重试保存');
    const local: MomentSyncConfig = { id: preset.id, userId, version: result.version,
      fingerprint: await fingerprint(snapshot), structureFingerprint: await structureFingerprint(preset, snapshot, momentConversation(preset.id, conversations, providers)),
      sourceRevision: revision, enabled: snapshot !== null, synced: true, blocked: false };
    await db.transaction('rw', db.presets, db.momentSyncConfigs, async () => {
      active(userId);
      await putPreset(preset);
      await db.momentSyncConfigs.put(local);
    });
  });
}

async function synchronizeConfigs(userId: string): Promise<void> {
  const db = getDb();
  const [{ configs }, presets, conversations, providers] = await Promise.all([
    request<{ configs: MomentConfigState[] }>(userId, '/configs', 'GET'),
    db.presets.toArray(), db.conversations.toArray(), db.providers.toArray(),
  ]);
  active(userId);
  const remote = new Map(configs.map(config => [config.presetId, config]));
  const owned = await db.momentSyncConfigs.where('userId').equals(userId).toArray();
  const ids = new Set([...presets.filter(automationEnabled).map(preset => preset.id), ...owned.map(row => row.id)]);
  const failures: string[] = [];
  for (const id of ids) {
    active(userId);
    try {
    let local = await db.momentSyncConfigs.get(id);
    if (local && local.userId !== userId) continue;
    const preset = presets.find(row => row.id === id);
    const snapshot = preset && automationEnabled(preset) ? await buildMomentSnapshot(preset, conversations, providers) : null;
    if (preset && automationEnabled(preset) && !snapshot) throw new Error('请在角色编辑页的模型配置中选择可用的文字模型');
    if (snapshot) validateSnapshot(snapshot);
    const enabled = snapshot !== null;
    const hash = await fingerprint(snapshot);
    const conversation = momentConversation(id, conversations, providers);
    // Context changes must never authorize this device to undo another device's
    // role edit. Hash raw settings rather than keyword-selected worldbook output.
    const structureHash = await structureFingerprint(preset, snapshot, conversation);
    const cloud = remote.get(id);
    if (!local) {
      if (!enabled) continue;
      const initial: MomentSyncConfig = { id, userId, version: cloud?.version ?? 0, fingerprint: hash,
        structureFingerprint: structureHash,
        sourceRevision: newId(), enabled, synced: cloud?.enabled === true, blocked: cloud?.enabled === true };
      await db.transaction('rw', db.momentSyncConfigs, async () => {
        if (!(await db.momentSyncConfigs.get(id))) await db.momentSyncConfigs.add(initial);
      });
      local = await db.momentSyncConfigs.get(id);
      if (local?.userId !== userId) continue;
    }
    const changed = local.fingerprint !== hash || local.enabled !== enabled;
    const structureChanged = local.structureFingerprint !== undefined && local.structureFingerprint !== structureHash;
    if (cloud && cloud.version !== local.version) {
      const accepted = cloud.sourceRevision === local.sourceRevision;
      local = { ...local, version: cloud.version, synced: accepted || !changed, blocked: !accepted };
      await db.momentSyncConfigs.put(local);
    }
    if (changed) {
      if (local.blocked && !structureChanged) {
        await db.momentSyncConfigs.put({ ...local, fingerprint: hash, structureFingerprint: structureHash });
        continue;
      }
      local = { ...local, version: cloud?.version ?? local.version, fingerprint: hash, enabled,
        structureFingerprint: structureHash,
        sourceRevision: newId(), synced: false, blocked: false };
      await db.momentSyncConfigs.put(local);
    }
    // Transient server tables are deliberately omitted from long-term backups.
    // An authoritative absence after a restore starts a fresh CAS generation.
    if (!cloud && (local.version > 0 || local.synced || local.blocked)) {
      local = { ...local, version: 0, sourceRevision: newId(), synced: false, blocked: false };
      await db.momentSyncConfigs.put(local);
    }
    // An expired configuration can be recreated when the user returns. A remote
    // edit remains authoritative until this device makes an actual local edit.
    if (!changed && local.synced && enabled && cloud?.enabled === false && cloud.sourceRevision === local.sourceRevision) {
      local = { ...local, sourceRevision: newId(), synced: false, blocked: false };
      await db.momentSyncConfigs.put(local);
    }
    if (local.synced || local.blocked) continue;
    try {
      const result = await request<{ version: number }>(userId, `/configs/${encodeURIComponent(id)}`, enabled ? 'PUT' : 'DELETE', {
        expectedVersion: local.version, sourceRevision: local.sourceRevision, ...(snapshot ? { snapshot } : {}),
      });
      active(userId);
      await db.transaction('rw', db.momentSyncConfigs, async () => {
        const latest = await db.momentSyncConfigs.get(id);
        if (latest?.sourceRevision === local?.sourceRevision) await db.momentSyncConfigs.put({ ...latest, version: result.version, synced: true });
      });
    } catch (error) {
      if (error instanceof ApiClientError && error.status === 409) {
        await db.momentSyncConfigs.put({ ...local, blocked: true });
        continue;
      }
      throw error;
    }
    } catch (error) {
      active(userId);
      failures.push(`${presets.find(row => row.id === id)?.name ?? id}：${error instanceof Error ? error.message : '同步失败'}`);
    }
  }
  if (failures.length) throw new Error(failures.join('；'));
}

async function uploadSchedules(userId: string): Promise<void> {
  const db = getDb();
  const events = (await db.automationEvents.toArray()).filter(row => row.kind !== 'bubble' && (row.status === 'queued' || row.status === 'running'));
  const failures: string[] = [];
  for (const event of events) {
    active(userId);
    try {
    if (event.userId && event.userId !== userId) continue;
    const config = await db.momentSyncConfigs.get(event.presetId);
    if (config?.userId !== userId) continue;
    const preset = await db.presets.get(event.presetId);
    if (!preset || !automationAllowsMoment(preset.automationMode)) {
      await db.automationEvents.update(event.id, { userId, status: 'skipped', updatedAt: Date.now() });
      continue;
    }
    if (event.status === 'running') {
      await db.automationEvents.update(event.id, { userId, status: 'skipped', updatedAt: Date.now(), error: '升级前已开始的朋友圈生成结果未知，未自动重发' });
      continue;
    }
    if (!config.enabled) {
      await db.automationEvents.update(event.id, { userId, status: 'skipped', updatedAt: Date.now() });
      continue;
    }
    if (!config.synced || config.blocked || config.version < 1) continue;
    await db.automationEvents.update(event.id, { userId });
    await request(userId, '/schedules', 'POST', {
      presetId: event.presetId, version: config.version, sourceId: event.id,
      kind: event.kind === 'moment' ? 'publish' : 'check', minutes: Math.max(0, (event.dueAt - Date.now()) / 60_000),
    });
    active(userId);
    await db.automationEvents.update(event.id, { status: 'done', updatedAt: Date.now(), error: undefined });
    } catch (error) {
      active(userId);
      failures.push(`${event.presetId}：${error instanceof Error ? error.message : '任务同步失败'}`);
    }
  }
  if (failures.length) throw new Error(failures.join('；'));
}

async function persistentImage(source: string): Promise<string> {
  const embedded = /^data:image\/[^;,]+;base64,([a-z0-9+/=\s]+)$/i.exec(source);
  if (embedded) {
    if (source.length > 34 * 1024 * 1024) throw new Error('Moment image is too large');
    const decoded = atob(embedded[1]!.replace(/\s/g, ''));
    if (decoded.length === 0 || decoded.length > 24 * 1024 * 1024) throw new Error('Invalid moment image');
    return source;
  }
  if (!/^https?:\/\//i.test(source)) throw new Error('Invalid moment image');
  const response = await fetch(source, { cache: 'no-store', credentials: 'omit' });
  if (!response.ok) throw new Error('Moment image download failed');
  const blob = await response.blob();
  if (!blob.type.startsWith('image/') || blob.size === 0 || blob.size > 24 * 1024 * 1024) throw new Error('Invalid moment image');
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => typeof reader.result === 'string' ? resolve(reader.result) : reject(new Error('Moment image read failed'));
    reader.onerror = () => reject(new Error('Moment image read failed'));
    reader.readAsDataURL(blob);
  });
}

/** The receipt is also a durable tombstone: retries never recreate a deleted post. */
export async function persistMomentDelivery(userId: string, delivery: MomentDelivery, receivingDeviceId: string): Promise<void> {
  active(userId);
  const db = getDb();
  const id = `${userId}:${delivery.id}`;
  const existing = await db.momentReceipts.get(id);
  const images = existing ? [] : await Promise.all(delivery.images.map(persistentImage));
  const avatar = !existing && delivery.authorAvatar && /^(https?:\/\/|data:image\/)/i.test(delivery.authorAvatar)
    ? await persistentImage(delivery.authorAvatar) : delivery.authorAvatar;
  active(userId);
  await db.transaction('rw', db.moments, db.momentReceipts, async () => {
    active(userId);
    const receipt = await db.momentReceipts.get(id);
    if (!receipt) {
      if (!(await db.moments.get(id))) await db.moments.add({ id, userId, presetId: delivery.presetId, content: delivery.content, images,
        authorName: delivery.authorName, ...(avatar ? { authorAvatar: avatar } : {}), createdAt: delivery.createdAt });
    }
    await db.momentReceipts.put({ id, userId, postId: delivery.id, deviceId: receivingDeviceId,
      leaseToken: delivery.leaseToken, savedAt: receipt?.savedAt ?? Date.now(), acknowledged: false });
  });
}

async function acknowledgePending(userId: string): Promise<void> {
  const db = getDb();
  const receipts = await db.momentReceipts.where('userId').equals(userId).filter(row => !row.acknowledged).toArray();
  for (const receipt of receipts) {
    try {
      await request(userId, '/ack', 'POST', { id: receipt.postId, deviceId: receipt.deviceId, leaseToken: receipt.leaseToken });
      active(userId);
      await db.momentReceipts.update(receipt.id, { acknowledged: true });
    } catch (error) {
      if (!(error instanceof ApiClientError && error.status === 409)) throw error;
    }
  }
}

export async function receiveCloudMoments(userId: string): Promise<void> {
  await acknowledgePending(userId);
  const receivingDeviceId = deviceId();
  const { posts } = await request<{ posts: MomentDelivery[] }>(userId, '/claim', 'POST', { deviceId: receivingDeviceId });
  active(userId);
  let failure: Error | undefined;
  for (const delivery of posts) {
    try { await persistMomentDelivery(userId, delivery, receivingDeviceId); }
    catch (error) { failure ??= error instanceof Error ? error : new Error('朋友圈保存失败'); }
  }
  await acknowledgePending(userId);
  if (failure) throw failure;
}

export function syncCloudMoments(): Promise<void> {
  const db = getDb();
  const pending = inFlight.get(db);
  if (pending) return pending;
  return serialized(db, async () => {
    const userId = getMomentAccountId();
    if (!userId || (typeof navigator !== 'undefined' && !navigator.onLine)) return;
    let configError: Error | undefined;
    try {
      if (document.visibilityState === 'visible') await synchronizeConfigs(userId);
    }
    catch (error) { configError = error instanceof Error ? error : new Error('朋友圈配置同步失败'); }
    try { await uploadSchedules(userId); }
    catch (error) { configError ??= error instanceof Error ? error : new Error('朋友圈任务同步失败'); }
    reportSyncError(userId, configError);
    await receiveCloudMoments(userId);
    if (configError) throw configError;
  });
}

/** Activity is deliberately separate from polling and data-change synchronization. */
export function startMomentSync(): () => void {
  let stopped = false;
  let changeTimer: ReturnType<typeof setTimeout> | undefined;
  let lastActivity = 0;
  const sync = () => { if (!stopped) void syncCloudMoments().catch(() => undefined); };
  const foreground = () => document.visibilityState === 'visible';
  const activity = async () => {
    if (stopped || !foreground()) return;
    try {
      const user = await apiClient.currentUser();
      if (stopped || user.status === 'disabled') return;
      active(user.id);
      if (Date.now() - lastActivity >= ACTIVITY_INTERVAL_MS) {
        await request(user.id, '/activity', 'POST', {});
        lastActivity = Date.now();
      }
      sync();
    } catch { /* Offline state retains account isolation and pending receipts. */ }
  };
  const onForeground = () => { if (foreground()) void activity(); };
  const onAccount = (event: Event) => {
    const userId = (event as CustomEvent<{ userId: string | null }>).detail.userId;
    const changed = getMomentAccountId() !== userId;
    setMomentAccountId(userId);
    if (changed) {
      lastActivity = 0;
      if (userId && foreground()) void activity();
    }
    if (userId && foreground()) sync();
  };
  const unsubscribeAccount = subscribeMomentAccount(sync);
  window.addEventListener('zhixin:account-change', onAccount);
  window.addEventListener('focus', onForeground);
  window.addEventListener('online', onForeground);
  document.addEventListener('visibilitychange', onForeground);
  window.addEventListener('storage', onForeground);
  const subscription = liveQuery(async () => {
    const db = getDb();
    // Reading role data only keeps delivery/receipt writes from recursively syncing.
    await Promise.all([db.presets.toArray(), db.conversations.toArray(), db.providers.toArray(), db.messages.toArray(), db.automationEvents.toArray()]);
    return Date.now();
  }).subscribe({ next: () => {
    clearTimeout(changeTimer);
    changeTimer = setTimeout(sync, 500);
  }, error: () => undefined });
  const syncTimer = setInterval(sync, SYNC_INTERVAL_MS);
  const activityTimer = setInterval(onForeground, ACTIVITY_INTERVAL_MS);
  void activity();
  return () => {
    stopped = true; clearTimeout(changeTimer); clearInterval(syncTimer); clearInterval(activityTimer);
    subscription.unsubscribe(); unsubscribeAccount();
    window.removeEventListener('zhixin:account-change', onAccount);
    window.removeEventListener('focus', onForeground); window.removeEventListener('online', onForeground);
    window.removeEventListener('storage', onForeground); document.removeEventListener('visibilitychange', onForeground);
  };
}
